import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useToastContext } from '@contexts/ToastContext';
import { useLinkSharing, type ShareLink } from '@components/modals/hooks/useLinkSharing';
import { CopyIcon, TrashIcon, GlobeIcon, LockIcon } from '@components/common/ui/icons';
import * as styles from '@components/modals/ShareModal.module.css';
import * as linkStyles from '@components/modals/LinkSharing.module.css';

const cn = (...classes: (string | undefined | false)[]) => classes.filter(Boolean).join(' ');

const getExpiryPresets = (t: TFunction) => [
    { label: t('share.link.expiry.never'), value: null },
    { label: t('share.link.expiry.oneDay'), value: 1 },
    { label: t('share.link.expiry.sevenDays'), value: 7 },
    { label: t('share.link.expiry.thirtyDays'), value: 30 },
];

function formatExpiry(expiresAt: string | null, t: TFunction): string {
    if (!expiresAt) return t('share.link.expiry.noExpiry');
    const date = new Date(expiresAt);
    const now = new Date();
    if (date < now) return t('share.link.expiry.expired');
    const diffMs = date.getTime() - now.getTime();
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays === 1) return t('share.link.expiry.expiresToday');
    return t('share.link.expiry.expiresInDays', { count: diffDays });
}

interface LinkItemProps {
    link: ShareLink;
    onDelete: (token: string) => void;
    loading: boolean;
}

const LinkItem: React.FC<LinkItemProps> = ({ link, onDelete, loading }) => {
    const { t } = useTranslation();
    const toast = useToastContext();

    const copyLink = async () => {
        try {
            await navigator.clipboard.writeText(link.shareLink);
            toast.success(t('share.toast.linkCopied'));
        } catch {
            toast.error(t('share.toast.linkCopyFailed'));
        }
    };

    return (
        <div className={linkStyles.linkItem}>
            <div className={linkStyles.linkItemMeta}>
                {link.name && <span className={linkStyles.linkName}>{link.name}</span>}
                <div className={linkStyles.linkItemBadges}>
                    <span className={cn(linkStyles.permBadge, link.permission === 'edit' && linkStyles.permBadgeEdit)}>
                        {link.permission === 'edit'
                            ? t('share.permission.editFull')
                            : t('share.permission.viewFull')}
                    </span>
                    <span className={linkStyles.expiryText}>{formatExpiry(link.expiresAt, t)}</span>
                </div>
            </div>
            <div className={linkStyles.linkItemRow}>
                <input
                    type="text"
                    className={cn(styles.input, linkStyles.linkInput)}
                    value={link.shareLink}
                    readOnly
                />
                <button
                    className={linkStyles.iconBtn}
                    onClick={copyLink}
                    title={t('share.link.copyTitle')}
                >
                    <CopyIcon className={linkStyles.iconBtnIcon} />
                </button>
                <button
                    className={cn(linkStyles.iconBtn, linkStyles.iconBtnDanger)}
                    onClick={() => onDelete(link.token)}
                    disabled={loading}
                    title={t('share.link.revokeTitle')}
                >
                    <TrashIcon className={linkStyles.iconBtnIcon} />
                </button>
            </div>
        </div>
    );
};

interface LinkSharingProps {
    noteId: string;
    open: boolean;
}

export const LinkSharing: React.FC<LinkSharingProps> = ({ noteId, open }) => {
    const { t } = useTranslation();
    const { links, loading, createLink, deleteLink } = useLinkSharing(noteId, open);
    const [permission, setPermission] = useState<'read' | 'edit'>('read');
    const [expiryDays, setExpiryDays] = useState<number | null>(null);
    const [linkName, setLinkName] = useState('');
    const [creating, setCreating] = useState(false);

    const expiryPresets = useMemo(() => getExpiryPresets(t), [t]);

    const handleCreate = async () => {
        setCreating(true);
        const expiresAt = expiryDays !== null
            ? new Date(Date.now() + expiryDays * 24 * 60 * 60 * 1000)
            : null;
        const result = await createLink(permission, expiresAt, linkName.trim());
        if (result) setLinkName('');
        setCreating(false);
    };

    return (
        <div className={cn(styles.section, styles.linkSection)}>
            <div className={styles.linkHeader}>
                <div>
                    <label className={styles.label}>{t('share.link.title')}</label>
                    <p className={styles.description}>{t('share.link.description')}</p>
                </div>
            </div>

            <div className={linkStyles.createRow}>
                <input
                    type="text"
                    className={cn(styles.input, linkStyles.nameInput)}
                    placeholder={t('share.link.namePlaceholder')}
                    value={linkName}
                    onChange={e => setLinkName(e.target.value)}
                    maxLength={60}
                />

                <div className={linkStyles.permToggle}>
                    <button
                        className={cn(linkStyles.permBtn, permission === 'read' && linkStyles.permBtnActive)}
                        onClick={() => setPermission('read')}
                    >
                        <LockIcon className={linkStyles.permBtnIcon} />
                        {t('share.permission.viewFull')}
                    </button>
                    <button
                        className={cn(linkStyles.permBtn, permission === 'edit' && linkStyles.permBtnActive)}
                        onClick={() => setPermission('edit')}
                    >
                        <GlobeIcon className={linkStyles.permBtnIcon} />
                        {t('share.permission.editFull')}
                    </button>
                </div>

                <div className={linkStyles.expiryGroup}>
                    <span className={linkStyles.expiryLabel}>{t('share.link.expiry.label')}</span>
                    <div className={linkStyles.expiryPresets}>
                        {expiryPresets.map(p => (
                            <button
                                key={String(p.value)}
                                className={cn(linkStyles.expiryBtn, expiryDays === p.value && linkStyles.expiryBtnActive)}
                                onClick={() => setExpiryDays(p.value)}
                            >
                                {p.label}
                            </button>
                        ))}
                    </div>
                </div>

                <button
                    className={cn(styles.button, styles.buttonPrimary, linkStyles.createBtn)}
                    onClick={handleCreate}
                    disabled={loading || creating}
                >
                    {t('share.link.create')}
                </button>
            </div>

            {links.length > 0 && (
                <div className={linkStyles.linkList}>
                    {links.map(link => (
                        <LinkItem
                            key={link.token}
                            link={link}
                            onDelete={deleteLink}
                            loading={loading}
                        />
                    ))}
                </div>
            )}

            {links.length === 0 && (
                <p className={linkStyles.emptyText}>{t('share.link.empty')}</p>
            )}
        </div>
    );
};
